import { AfterContentInit, ChangeDetectorRef, ElementRef, Injector, OnDestroy, Directive } from '@angular/core';
import { eventFindInPath, fiDebounce, isDefined } from '@fi-shared/utils';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { Subject } from 'rxjs';
import { FiContenteditableService } from '@fi-shared/fi-contenteditable/fi-contenteditable.service';
import { fromEvent } from 'rxjs';
import { ISelectedButtonsData } from '@fi-shared/fi-text-editor/fi-text-editor';
import { takeUntil } from 'rxjs/operators';
import { AppTextService } from '@fi-shared/app-text';
import { ITextItemLinkModel } from '@fi-shared/fi-text-item-helper/fi-text-item-link';

export enum TextPanelMenuTypes {
    Alignment,
    LinkAttachment,
}

export interface ITextPanelMenuItem {
    iconClass: string;
    label: string;
    value: string | number;
}

export interface ITextPanelButton {
    matMenuTriggerButtonType?: TextPanelMenuTypes;
    isMatMenu?: boolean;
    menuItems?: ITextPanelMenuItem[];
    title?: string;
    class?: string | ((arg: any) => string);
    btnClass?: string;
    callback?: (arg?: number | string) => void;
    selected?: () => boolean;
    isDisabled?: () => boolean;
}

export interface ITextPanelButtonsGroup {
    buttons: ITextPanelButton[];
    class?: string;
}

@Directive()
export class TextPanelBase implements AfterContentInit, OnDestroy {
    selectedButtonsData: ISelectedButtonsData = {
        bold: false,
        underline: false,
        italic: false,
        strikeThrough: false,
        superscript: false,
        subscript: false,
    };
    private contenteditableService: FiContenteditableService;
    protected cdr: ChangeDetectorRef;
    appTextService: AppTextService;
    private readonly textItemModelAttrName = 'text-item-model';
    currentElement: HTMLElement;
    elementRef: ElementRef<HTMLElement>;

    constructor(
        private injector: Injector,
        contentElementRef?: ElementRef,
        private isAdvancedPanel?: boolean,
    ) {
        this.contenteditableService = injector.get(FiContenteditableService);
        this.cdr = injector.get(ChangeDetectorRef);
        const fiBrowserEventsService = injector.get(FiBrowserEventsService);
        this.appTextService = injector.get(AppTextService);
        this.elementRef = injector.get(ElementRef);

        if (contentElementRef) {
            // subscribe to content element's events
            fromEvent(contentElementRef.nativeElement, 'mouseup')
                .pipe(takeUntil(this.ngOnDestroy$))
                .subscribe(() => this.setButtonsSelectedDebounced());
            fromEvent(contentElementRef.nativeElement, 'keyup')
                .pipe(takeUntil(this.ngOnDestroy$))
                .subscribe(() => this.setButtonsSelectedDebounced());
        } else {
            fiBrowserEventsService
                .getDocumentEventSubject$('mouseup')
                .pipe(takeUntil(this.ngOnDestroy$))
                .subscribe(() => this.setButtonsSelectedDebounced());
            fiBrowserEventsService
                .getDocumentEventSubject$('keyup')
                .pipe(takeUntil(this.ngOnDestroy$))
                .subscribe(() => this.setButtonsSelectedDebounced());
        }
    }

    protected ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    ngAfterContentInit(): void {
        this.setButtonsSelectedDebounced();
    }

    private setButtonsSelectedDebounced = fiDebounce(() => this.setButtonsSelected(), 200);

    private setButtonsSelected() {
        if (this.preventSelectedButtonsUpdate) {
            this.preventSelectedButtonsUpdate = false;
            return;
        }

        const el: any = this.contenteditableService.getRangeStartElement();
        if (el) {
            this.contenteditableService.updateSelectedButtonsData(
                this.selectedButtonsData,
                el,
                this.isAdvancedPanel,
                '.text-editor',
            );
            this.cdr.markForCheck();
        }
    }
    private preventSelectedButtonsUpdate = false;

    changeTextStyles(fontStyleButton: string, showDefaultUI: boolean, valueArgument: string) {
        document.execCommand(fontStyleButton, showDefaultUI, valueArgument);
        this.selectedButtonsData[fontStyleButton] = !this.selectedButtonsData[fontStyleButton];
        this.preventSelectedButtonsUpdate = true;
    }

    isMenuItemHidden(button: ITextPanelButton, menuItem: ITextPanelMenuItem): boolean {
        if (!this.isLinkMenu(button.matMenuTriggerButtonType)) return false;

        if (isDefined(this.textLinkItem)) return menuItem.value !== this.getTextLinkItemData().Type;

        return false;
    }

    getMatMenuItemLabel(button: ITextPanelButton, menuItem: ITextPanelMenuItem) {
        if (!this.isLinkMenu(button.matMenuTriggerButtonType)) return this.appTextService.get(menuItem.label);

        return isDefined(this.textLinkItem)
            ? this.getTextLinkItemData().Value.Name || this.getTextLinkItemData().Value
            : this.appTextService.get(menuItem.label);
    }

    isUnlinkButtonShown(): boolean {
        return isDefined(this.textLinkItem);
    }

    getTextLinkItemData(): ITextItemLinkModel {
        return JSON.parse(this.textLinkItem.getAttribute(this.textItemModelAttrName));
    }

    isLinkMenu(matMenuTriggerButtonType: TextPanelMenuTypes): boolean {
        return matMenuTriggerButtonType === TextPanelMenuTypes.LinkAttachment;
    }

    textLinkItem: HTMLElement;
    setTextLinkItem($event: MouseEvent) {
        $event.stopPropagation();
        this.textLinkItem = this.currentElement
            ? eventFindInPath({ target: this.currentElement }, 'a[ccm-text-item-link]')
            : undefined;
    }

    unlink() {
        const spanElement = document.createElement('span');
        spanElement.innerHTML = this.textLinkItem.innerHTML;
        this.textLinkItem.parentElement.replaceChild(spanElement, this.textLinkItem);
    }

    setIsDisabledPropButtons(btnGroups: ITextPanelButtonsGroup[]) {
        btnGroups.forEach((buttonGroup) => {
            buttonGroup.buttons.forEach((button) => {
                button.isDisabled = () => this.isCurrentElementMedia();
            });
        });
    }

    isCurrentElementMedia(): boolean {
        return (
            this.currentElement?.matches('[ccm-text-item-media-model]') ||
            this.currentElement?.parentElement?.matches('[ccm-text-item-media-model]')
        );
    }
}
